<template>
  <ConfigProvider :locale="getAntdLocale">
    <AppProvider>
      <RouterView />
      <GlobalCmdK />
    </AppProvider>
  </ConfigProvider>
</template>

<script lang="ts" setup>
  // import { provide } from 'vue';
  // import { DefaultApolloClient } from '@vue/apollo-composable';
  import { onBeforeUnmount, onMounted } from 'vue';
  import { ConfigProvider } from 'ant-design-vue';
  import { AppProvider } from '/@/components/Application';
  import { useTitle } from '/@/hooks/web/useTitle';
  import { useLocale } from '/@/locales/useLocale';
  import { defHttp } from '/@/utils/http/axios';
  import { useUserStoreWithOut } from '/@/store/modules/user';
  import GlobalCmdK from '/@/components/GlobalCmdK.vue';
  // support Multi-language
  const { getAntdLocale } = useLocale();
  useTitle();

  // ── presence heartbeat ─────────────────────────────────────────────
  // Pings /user/heartbeat every 60s while a session is open so the
  // backend can mark this user as "online" (last_active_at within 5m
  // window). Skipped if there's no auth token (login / reset pages).
  const userStore = useUserStoreWithOut();
  let beatTimer: ReturnType<typeof setInterval> | null = null;
  let lastInteraction = Date.now();
  function markInteraction() { lastInteraction = Date.now(); }
  function sendHeartbeat() {
    if (!userStore.getToken || document.visibilityState !== 'visible'
      || Date.now() - lastInteraction > 5 * 60_000) return;
    defHttp
      .post({ url: '/user/heartbeat', headers: { ignoreCancelToken: true } })
      .catch(() => { /* swallow — heartbeat is best-effort */ });
  }
  function onVisibility() {
    if (document.visibilityState === 'visible') { markInteraction(); sendHeartbeat(); }
  }
  onMounted(() => {
    sendHeartbeat();
    beatTimer = setInterval(sendHeartbeat, 60_000);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointerdown', markInteraction);
    window.addEventListener('keydown', markInteraction);
    window.addEventListener('scroll', markInteraction, true);
  });
  onBeforeUnmount(() => {
    if (beatTimer) clearInterval(beatTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pointerdown', markInteraction);
    window.removeEventListener('keydown', markInteraction);
    window.removeEventListener('scroll', markInteraction, true);
  });
</script>
